// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseButton, BaseInput, toast} from "@/components";
import useGetUserInfo from "../../../hooks/useGetUserInfo";
import useUpdateProfile from "../../../hooks/useUpdateProfile";
import {useBalanceStore} from "../../../store/useBalance.store";
import BaseModal from "../index";

const buildSchema = t =>
  z.object({
    cedula: z
      .string()
      .trim()
      .min(1, t("pagoMovilRegistrationCedulaMessage"))
      .regex(/^[0-9]+$/, t("pagoMovilRegistrationCedulaValidation"))
      .min(7, t("pagoMovilRegistrationCedulaMinLength"))
      .max(15, t("pagoMovilRegistrationCedulaMaxLength")),
    phoneNumber: z
      .string()
      .trim()
      .min(1, t("pagoMovilRegistrationPhoneNumberMessage"))
      .regex(/^[0-9+\-\s()]+$/, t("pagoMovilRegistrationPhoneValidation"))
      .min(10, t("pagoMovilRegistrationPhoneMinLength")),
  });

const PagoMovilRegistrationModal = () => {
  const {t} = useTranslation();
  const pagoMovilRegistrationModalVisible = useBalanceStore(state => state.pagoMovilRegistrationModalVisible);
  const handlePagoMovilRegistrationModal = useBalanceStore(state => state.handlePagoMovilRegistrationModal);
  const {data: userData, refetch: refetchUserData} = useGetUserInfo();

  const {postUpdate, isPending, error} = useUpdateProfile(() => {
    toast.success(`${t("pagoMovilRegistrationSuccessTitle")} — ${t("pagoMovilRegistrationSuccessMessage")}`);
    handlePagoMovilRegistrationModal();
    refetchUserData();
  });

  const [values, setValues] = useState({cedula: "", phoneNumber: ""});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (pagoMovilRegistrationModalVisible) {
      setValues({
        cedula: userData?.nationalID || "",
        phoneNumber: userData?.phoneNumber || "",
      });
      setErrors({});
    }
  }, [pagoMovilRegistrationModalVisible, userData]);

  useEffect(() => {
    if (error) {
      toast.error(`${t("pagoMovilRegistrationErrorTitle")} — ${t("pagoMovilRegistrationErrorMessage")}`);
    }
  }, [error, t]);

  const handleChange = field => e => {
    setValues(v => ({...v, [field]: e.target.value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    postUpdate({
      nationalID: result.data.cedula,
      phoneNumber: result.data.phoneNumber,
    });
  };

  const handleClose = () => {
    setErrors({});
    handlePagoMovilRegistrationModal();
  };

  return (
    <BaseModal
      title={t("pagoMovilRegistrationTitle")}
      state={pagoMovilRegistrationModalVisible}
      closeModal={handleClose}
      width="500px"
    >
      <div style={{padding: "20px 0"}}>
        <p
          style={{
            fontSize: "var(--font-h3)",
            marginBottom: "var(--space-5)",
            color: "var(--color-text-primary)",
          }}
        >
          {t("pagoMovilRegistrationMessage")}
        </p>
        <form
          onSubmit={handleSubmit}
          noValidate
        >
          <div style={{marginBottom: "var(--space-4-5)"}}>
            <label
              htmlFor="pm-cedula"
              style={{display: "block", marginBottom: "var(--space-2)", fontWeight: 500}}
            >
              {t("pagoMovilRegistrationCedula")}
            </label>
            <BaseInput
              id="pm-cedula"
              placeholder={t("pagoMovilRegistrationCedulaPlaceholder")}
              value={values.cedula}
              onChange={handleChange("cedula")}
              error={errors.cedula}
            />
          </div>

          <div style={{marginBottom: "var(--space-4-5)"}}>
            <label
              htmlFor="pm-phone"
              style={{display: "block", marginBottom: "var(--space-2)", fontWeight: 500}}
            >
              {t("pagoMovilRegistrationPhoneNumber")}
            </label>
            <BaseInput
              id="pm-phone"
              placeholder={t("pagoMovilRegistrationPhoneNumberPlaceholder")}
              value={values.phoneNumber}
              onChange={handleChange("phoneNumber")}
              error={errors.phoneNumber}
            />
          </div>

          <div style={{display: "flex", gap: "var(--space-3)", justifyContent: "flex-end", marginTop: 30}}>
            <BaseButton
              type="button"
              variant="secondary"
              onClick={handleClose}
            >
              {t("pagoMovilRegistrationCancelBtn")}
            </BaseButton>
            <BaseButton
              type="submit"
              variant="primary"
              loading={isPending}
            >
              {t("pagoMovilRegistrationSubmitBtn")}
            </BaseButton>
          </div>
        </form>
      </div>
    </BaseModal>
  );
};

export default PagoMovilRegistrationModal;
